import React, { createContext, useContext, useState, ReactNode } from "react";
import apiClient from "../services/apiClient";
import { API_ROUTES } from "../constants/apiRoutes";

interface AuthContextType {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  async function login(username: string, password: string) {
    try {
      console.log("AuthContext: Starting login process...");
      
      // Backend login
      const response = (await apiClient(API_ROUTES.AUTH.LOGIN, {
        method: "POST",
        body: { username, password, role: "admin" },
      })) as any;

      console.log("AuthContext: Backend response received:", response);

      if (!response?.data?.token) {
        throw new Error("Invalid credentials - no token received from server");
      }

      // Store backend token
      sessionStorage.setItem("token", response.data.token);
      console.log("AuthContext: Backend token stored");

      // Optional WordPress/WooCommerce login - don't fail the entire login if this fails
      try {
        console.log("AuthContext: Attempting WordPress login...");
        
        const wpResponse = await fetch(API_ROUTES.AUTH.WORDPRESS_LOGIN, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
        });

        if (wpResponse.ok) {
          const wpData = await wpResponse.json();
          sessionStorage.setItem("wp_token", wpData.token);
          console.log("AuthContext: WordPress token stored");
        } else {
          const wpError = await wpResponse.json();
          console.warn("WordPress Login failed (non-critical):", wpError.message);
          // Don't throw here - WordPress login failure shouldn't block main login
        }
      } catch (wpErr) {
        console.warn("WordPress API connection failed (non-critical):", wpErr);
        // Don't throw here - WordPress connection failure shouldn't block main login
      }

      setIsAuthenticated(true);
      console.log("AuthContext: Login successful");

    } catch (error: any) {
      console.error("AuthContext: Login failed:", error);
      
      // Clean up any partial authentication state
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("wp_token");
      setIsAuthenticated(false);
      
      // Create a more user-friendly error message
      let errorMessage = "Authentication failed";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = "Invalid username or password";
      } else if (error.response?.status === 403) {
        errorMessage = "Access denied";
      } else if (error.response?.status >= 500) {
        errorMessage = "Server error. Please try again later.";
      }
      
      // Make sure we throw the error so AdminLogin can handle it
      throw new Error(errorMessage);
    }
  }

  function logout() {
    console.log("AuthContext: Logging out...");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("wp_token");
    sessionStorage.removeItem("wooToken");
    sessionStorage.removeItem("wooUser");
    setIsAuthenticated(false);
  }

  const value = {
    login,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}